import type { UseQueryResult } from '@tanstack/react-query'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { DefaultLayout } from '@/shared/ui/DefaultLayout'
import { QueryState } from '@/shared/ui/QueryState'
import { api } from '@/shared/lib/api'
import { PUBLIC_STALE_TIME_MS } from '@/shared/lib/query'
import type { ProblemCard } from '@/shared/lib/apiTypes'
import { useCardLabels } from '@/shared/lib/cardLabels'
import { makeLabeler, useCommonCodes } from '@/shared/lib/codes'
import { formatDate } from '@/shared/lib/format'
import { CARD_CATEGORY_MAX } from '@/shared/constants/cards'
import { RECENT_COUNT } from '@/public/features/problem/constants/list'
import { useProblemMatches } from '@/public/features/problem/composables/problemMatches'
import { AiKeywordMap } from '@/public/features/problem/ui/problemDetailPc/AiKeywordMap'
import { ProblemIssueCard } from '@/shared/ui/IssueCard'
import { IssueIcon } from '@/public/features/home/ui/HomeVariants'
import { CarouselArrows } from '@/shared/ui/CarouselArrows'
import { useRowCarousel } from '@/shared/composables/useRowCarousel'
import { CityIcon } from '@/shared/ui/icons/CityIcon'
import { InfoIcon } from '@/shared/ui/icons/InfoIcon'
import { LocationPinIcon } from '@/shared/ui/icons/LocationPinIcon'
import { ChevronRightIcon } from '@/shared/ui/icons/ChevronRightIcon'
import { TARGET_ICON } from '@/shared/ui/icons/target/targetIcons'

export function ProblemDetailPc({
  id,
  query,
}: {
  id: string | undefined
  query: UseQueryResult<ProblemCard>
}) {
  const { targetLabel, categoryLabel, isReady } = useCardLabels()
  const orgTypeLabel = makeLabeler(useCommonCodes('org_type').data)
  const matches = useProblemMatches(id)
  const recentCarousel = useRowCarousel()

  const recent = useQuery({
    queryKey: ['problem-cards', 'recent'],
    queryFn: () => api.problemCards({ limit: 100 }),
    staleTime: PUBLIC_STALE_TIME_MS,
  })

  const p = query.data
  const items = matches.data?.items ?? []

  const regionCode = p?.region.code
  const regionTotal = useQuery({
    queryKey: ['problem-cards', 'region-total', regionCode],
    queryFn: () => api.problemCards({ region: regionCode ?? '', limit: 1 }),
    staleTime: PUBLIC_STALE_TIME_MS,
    enabled: regionCode !== undefined,
  })

  return (
    <DefaultLayout tree="pc" className="[--body-w:1000px]">
      <QueryState isPending={query.isPending || !isReady} error={query.error}>
        {p === undefined ? null : (
          <div className="layout-body pt-8 pb-[60px]">
            <nav className="flex items-center gap-2 text-body-sm font-medium text-black-100">
              <Link className="text-inherit no-underline" to="/problems">
                시민문제
              </Link>
              <ChevronRightIcon size={16} />
              <span>{p.title}</span>
            </nav>

            <div className="mt-6 flex items-start gap-11">
              <div className="flex w-[640px] shrink-0 flex-col gap-[60px]">
                <section className="flex flex-col gap-6">
                  <div className="flex flex-col gap-2.5">
                    <div className="flex flex-col gap-2">
                      <p className="m-0 flex items-center gap-1 text-body-sm text-black-300">
                        <span className="text-white-600">
                          <LocationPinIcon size={20} />
                        </span>
                        {p.region.name}
                      </p>
                      <h1 className="m-0 text-[20px] leading-[1.7] font-semibold tracking-[-0.1px] text-black-800">
                        {p.title}
                      </h1>
                    </div>
                    {p.summary !== null && p.summary.length > 0 && (
                      <p className="m-0 text-body-md-reading font-normal text-black-800">{p.summary}</p>
                    )}
                  </div>

                  <div className="flex items-center gap-[15px]">
                    <div className="flex min-w-px flex-1 items-center gap-2.5">
                      {(p.categoryCodes ?? []).slice(0, CARD_CATEGORY_MAX).map((code) => (
                        <span
                          key={code}
                          className="inline-flex shrink-0 items-center justify-center rounded-lg border border-[rgb(0_0_0/5%)] bg-[#ebebeb] px-[15px] py-1 text-body-sm font-medium whitespace-nowrap text-[#464350]"
                        >
                          {categoryLabel(code)}
                        </span>
                      ))}
                    </div>
                    <span className="shrink-0 text-body-sm font-normal whitespace-nowrap text-black-100">
                      {formatDate(p.createdAt)}
                    </span>
                  </div>
                </section>

                {p.keywords !== undefined && p.keywords.length > 0 && (
                  <AiKeywordMap
                    title={p.title}
                    keywords={p.keywords}
                    ranks={items
                      .slice(0, 2)
                      .map((m) => ({ title: m.solutionCardTitle, fitGradeLabel: m.fitGradeLabel }))}
                  />
                )}
              </div>

              <aside className="flex w-[316px] shrink-0 flex-col gap-[30px] self-stretch overflow-clip rounded-[18px] bg-[#f8f9fc] px-[26px] py-7">
                <div className="flex flex-col gap-7">
                  <section className="flex flex-col gap-2.5">
                    <h2 className="m-0 text-body-sm font-medium text-black-800">해결 대상</h2>
                    <div className="grid grid-cols-3 items-start gap-3">
                      {p.targetCodes.map((code) => {
                        const Icon = TARGET_ICON[code]
                        return (
                          <span
                            key={code}
                            className="flex flex-col items-center justify-center gap-2 rounded-lg px-3 py-1 text-black-300"
                          >
                            {Icon !== undefined && (
                              <span className="shrink-0" aria-hidden="true">
                                <Icon size={32} />
                              </span>
                            )}
                            <span className="text-body-md font-medium whitespace-nowrap">
                              {targetLabel(code)}
                            </span>
                          </span>
                        )
                      })}
                    </div>
                  </section>

                  {p.keywords !== undefined && p.keywords.length > 0 && (
                    <>
                      <span className="block h-px w-full rounded-full bg-white-300" aria-hidden="true" />
                      <section className="flex flex-col gap-3">
                        <h2 className="m-0 text-body-sm font-medium text-black-800">AI 추출 키워드</h2>
                        <div className="flex flex-wrap items-center gap-3">
                          {p.keywords.map((word) => (
                            <span
                              key={word}
                              className="inline-flex items-center justify-center rounded-lg bg-white-200 px-3 py-1 text-body-md font-medium whitespace-nowrap text-black-600"
                            >
                              # {word}
                            </span>
                          ))}
                        </div>
                      </section>
                    </>
                  )}
                </div>

                <div className="flex flex-1 flex-col justify-between overflow-clip rounded-[18px] border border-violet-100 bg-white-50 px-[18px] py-3.5">
                  <div className="flex items-center gap-2">
                    <div className="flex min-w-px flex-1 flex-col gap-1">
                      <p className="m-0 text-body-md font-medium text-black-600">우리 동네 시민 문제</p>
                      <p className="m-0 flex items-center gap-1 text-body-sm text-black-100">
                        <span className="text-white-500">
                          <LocationPinIcon size={20} />
                        </span>
                        {p.region.name}
                      </p>
                    </div>
                    {regionTotal.data !== undefined && (
                      <span className="flex shrink-0 items-center gap-1 whitespace-nowrap text-black-600">
                        <span className="text-headline-md font-semibold tracking-[-0.14px]">
                          {regionTotal.data.total}
                        </span>
                        <span className="text-body-md font-medium">건</span>
                      </span>
                    )}
                  </div>
                  <Link
                    className="flex items-center justify-end gap-2 text-body-sm-reading font-medium text-violet-700 no-underline"
                    to={`/problems?region=${p.region.code}`}
                  >
                    다른 시민문제 확인하기
                    <ChevronRightIcon size={20} />
                  </Link>
                </div>
              </aside>
            </div>

            <section className="mt-[60px] rounded-[18px] bg-violet-50 px-6 py-5">
              <h2 className="m-0 text-body-lg font-semibold text-black-600">추천 해결 카드</h2>
              {items.length === 0 ? (
                <p className="m-0 mt-4 text-body-md-reading text-black-100">
                  아직 적합한 해결 카드가 없어요.
                </p>
              ) : (
                <ul className="m-0 mt-4 grid list-none grid-cols-3 items-stretch gap-4 p-0">
                  {items.map((m) => (
                    <li key={m.candidateId} className="flex">
                      <Link
                        className="flex w-full flex-col gap-5 rounded-2xl border border-white-200 bg-white-50 px-6 py-[22px] text-inherit no-underline shadow-[0_0_4px_-1px_rgb(0_0_0/8%),0_0_12px_-4px_rgb(0_0_0/16%)]"
                        to={`/solutions/${m.solutionCardId}?from=${p.id}`}
                      >
                        <span className="flex w-full items-start gap-2">
                          <span className="flex min-w-px flex-1 items-center gap-2">
                            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[99px] border-[1.25px] border-[rgb(0_0_0/5%)] bg-violet-100 text-black-100 [&>svg]:opacity-50">
                              <CityIcon size={20} />
                            </span>
                            <span className="flex min-w-0 flex-col justify-center gap-[3px]">
                              <span className="truncate text-body-md-reading font-medium text-black-500">
                                {m.orgName}
                              </span>
                              <span className="truncate text-body-sm text-black-100">
                                {orgTypeLabel(m.orgTypeCode)}
                              </span>
                            </span>
                          </span>
                          <span className="flex shrink-0 flex-col items-center justify-center gap-0.5 rounded-[10px] bg-violet-900 px-3 py-1.5 whitespace-nowrap">
                            <span className="text-label font-medium text-white-500">적합도</span>
                            <span className="text-body-sm font-semibold text-white-50">
                              {m.fitGradeLabel ?? '확인 필요'}
                            </span>
                          </span>
                        </span>

                        <span className="flex w-full flex-col gap-[18px]">
                          <span className="flex w-full flex-col gap-2">
                            <span className="text-body-lg-reading font-semibold text-black-800">
                              {m.solutionCardTitle}
                            </span>
                            {m.solutionCardSummary !== null && m.solutionCardSummary.length > 0 && (
                              <span className="line-clamp-2 text-body-md-reading font-normal text-black-800">
                                {m.solutionCardSummary}
                              </span>
                            )}
                          </span>
                          {m.accessCondition !== null && m.accessCondition.length > 0 && (
                            <span className="flex items-center gap-1 text-body-sm font-normal text-violet-600">
                              <InfoIcon size={20} />
                              {m.accessCondition}
                            </span>
                          )}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="mt-[60px]">
              <div className="flex h-8 items-center">
                <h2 className="m-0 flex-1 text-body-lg font-semibold text-black-600">
                  최근 등록된 시민 문제
                </h2>
                <Link
                  className="flex items-center gap-2 text-body-sm font-medium text-black-100 no-underline"
                  to="/problems"
                >
                  더보기
                  <ChevronRightIcon size={12} />
                </Link>
              </div>
              <div className="relative mt-[18px]">
                <ul
                  ref={recentCarousel.ref}
                  className="m-0 flex list-none items-stretch gap-4 overflow-x-auto p-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                >
                  {(recent.data?.items ?? [])
                    .filter((item) => item.id !== p.id)
                    .slice(0, RECENT_COUNT)
                    .map((item, i) => (
                      <li key={item.id} className="flex w-[322.67px] min-w-0 shrink-0">
                        <Link
                          className="flex w-full min-w-0 text-inherit no-underline"
                          to={`/problems/${item.id}`}
                        >
                          <ProblemIssueCard
                            categories={(item.categoryCodes ?? [])
                              .slice(0, CARD_CATEGORY_MAX)
                              .map(categoryLabel)}
                            title={item.title}
                            summary={item.summary ?? undefined}
                            icon={<IssueIcon index={i} />}
                            metaItems={[formatDate(item.createdAt), item.region.name]}
                            {...(item.matchedSolutionCount !== null
                              ? {
                                  actionText: `적합카드 ${item.matchedSolutionCount}장`,
                                  actionMuted: item.matchedSolutionCount === 0,
                                }
                              : {})}
                          />
                        </Link>
                      </li>
                    ))}
                </ul>
                <CarouselArrows control={recentCarousel} label="최근 등록된 시민 문제" top={100} />
              </div>
            </section>
          </div>
        )}
      </QueryState>
    </DefaultLayout>
  )
}
